import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { share, map, first } from 'rxjs/operators';
import { Router } from '@angular/router';

import { AccountConfirmEmail } from '../../account/models/account-confirmemail';
import { AccountForgotPassword } from '../../account/models/account-forgotpassword';
import { AccountLogin } from '../../account/models/account-login';
import { AccountResetPassword } from '../../account/models/account-resetpassword';
import { LicenseAssignment } from '../../license/models/license-assignment';
import { AuthService } from './auth.service';
import { UtilityService } from './utilities.service';
import { User } from '../models/user';
import { RedemptionCode } from '../../redemption/models/redemption-code';
import { ValidationResult } from '../models/validation-result';
import { LicenseInfo } from '../models/license-info';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

/** Service to handle User actions. Use the auth service for authentication actions. */
@Injectable()
export class UserService {
  user$: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  login$: Observable<AccountLogin>;
  forgotPassword$: Observable<boolean>;
  resetPassword$: Observable<boolean>;
  confirmEmail$: Observable<boolean>;
  emailTaken$: Subject<boolean> = new Subject<boolean>();
  updateUser$: Observable<boolean>;
  errors$: Observable<string>;

  private _confirmEmailObserver: any;
  private _errorObserver: any;
  private _updateUserSuccess: any;
  // private _email: string;

  private _dataStore: {
    token: string;
    login: AccountLogin;
    forgotPasswordSuccess: boolean;
    resetPasswordSuccess: boolean;
    confirmEmailSuccess: boolean;
    userUpdatedSuccess: boolean;
  };

  accountCreationSteps = ['/pricing', '/createaccount'];
  accountCompletedStepIndex = -1;

  get stepProgressString() {
    const index = this.accountCreationSteps.findIndex(t => t === this._router.url);
    return (index + 1).toString() + ' of ' + this.accountCreationSteps.length.toString();
  }

  constructor(
    private _http: HttpClient,
    private _utilService: UtilityService,
    private _identityService: AuthService,
    private _router: Router
  ) {
    this.confirmEmail$ = new Observable<boolean>(
      observer => (this._confirmEmailObserver = observer)
    );
    this.errors$ = new Observable<string>(observer => (this._errorObserver = observer)).pipe(
      share()
    );
    this.updateUser$ = new Observable<boolean>(observer => (this._updateUserSuccess = observer));
    this._dataStore = {
      token: '',
      login: new AccountLogin(),
      forgotPasswordSuccess: false,
      resetPasswordSuccess: false,
      confirmEmailSuccess: false,
      userUpdatedSuccess: false,
    };

    // init the service's user if logged in
    this._identityService.isLoggedIn$.subscribe(loggedIn => {
      if (loggedIn) {
        this.fetchUser(this._identityService.userId)
          .pipe(first())
          .subscribe(user => {
            this.user$.next(user);
          });
      }
    });
  }

  // loadUser(id: number) {
  //     this.getUser(id).subscribe(data => {
  //         let updatedUser = data;
  //         this.user$.next(updatedUser);
  //         let added = false;
  //         this._dataStore.users.forEach((user, i) => {
  //             if (user.Id === updatedUser.Id) {
  //                 this._dataStore.users[i] = updatedUser;
  //                 added = true;
  //             }
  //         });
  //     });
  // }

  getUser(id: number) {
    return this.fetchUser(id);
  }

  private fetchUser(id: number) {
    return this._http.get('api/account/' + id).pipe(map(response => new User(response)));
  }

  getUserEmail(customerid: string, username: string) {
    const body = {
      CustomerId: customerid,
      Username: username,
    };
    const httpOptionsForm = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };
    return this._http
      .post('api/account/GetEmail', this._utilService.serialize(body), httpOptionsForm)
      .pipe(map(response => response as string));
  }

  // getUserInfo() {
  //     return this._http.get('api/account/GetUserInfo')
  //         .map(response => response.json());
  // }

  // getAccountLogin(user: User) {
  //     var headers = new Headers();
  //     var r = {
  //         username: user.UserName,
  //         password: user.Password,
  //         customerid: user.CustomerId,
  //         email: user.Email,
  //         redirected: user.Redirected
  //     };

  //     headers.append('Content-Type', 'application/x-www-form--urlencoded');
  //     return this._http.post('api/token', this._utilService.serialize(r), { headers: headers })
  //         .map(response => <AccountLogin>response.json());
  // }

  // private handleError(error: any) {
  //     console.error(error);
  // }

  setEulaAccepted(userId): Observable<boolean> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    return this._http
      .post('api/account/accepteula', JSON.stringify(userId), httpOptions)
      .pipe(map(data => data['isAccepted'] as boolean));
  }

  forgotPassword(account: AccountForgotPassword) {
    return this._http.post<any>('api/account/forgotpassword', JSON.stringify(account), httpOptions);
  }

  resetPassword(account: AccountResetPassword) {
    return this._http.post<any>('api/account/resetpassword', JSON.stringify(account), httpOptions);
  }

  confirmEmail(account: AccountConfirmEmail) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    this._http.post('api/account/confirmemail', JSON.stringify(account), httpOptions).subscribe(
      data => {
        if (data) {
          this._dataStore.confirmEmailSuccess = true;
          this._confirmEmailObserver.next(this._dataStore.confirmEmailSuccess);
        }
      }
      //err =>  console.log('Could not confirm email.')
    );
  }

  updateUser(user: User) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    this._http.post('api/account/' + user.Id, JSON.stringify(user), httpOptions).subscribe(
      data => {
        if (data) {
          this._dataStore.userUpdatedSuccess = true;
          this._updateUserSuccess.next(this._dataStore.userUpdatedSuccess);
        }
      },
      error => {
        this._errorObserver.next(JSON.parse(error['_body'])['Message']);
      }
    );
  }

  goToNextStep(): any {
    const index = this.accountCreationSteps.findIndex(t => t === this._router.url);
    if (index === this.accountCompletedStepIndex + 1) {
      this.accountCompletedStepIndex = index;
    }
    this._router.navigate([this.accountCreationSteps[this.accountCompletedStepIndex + 1]]);
  }

  isValidStep(): boolean {
    const index = this.accountCreationSteps.findIndex(t => t === this._router.url);
    return index <= this.accountCompletedStepIndex + 1;
  }

  createUser(user: User): Observable<User> {
    return this._http
      .post('api/account/create', JSON.stringify(user), httpOptions)
      .pipe(map(response => new User(response)));
  }

  isEmailUnique(email: string) {
    return this._http.post('api/account/isemailunique', JSON.stringify(email), httpOptions);
  }

  /**
   * Check if password is valid
   * @param password
   */
  isPasswordValid(password: string): Observable<ValidationResult> {
    return this._http.post<ValidationResult>(
      'api/account/password-valid',
      JSON.stringify(password),
      httpOptions
    );
  }

  isCodeValid(code: string) {
    return this._http.post('api/account/iscodevalid', JSON.stringify(code), httpOptions);
  }

  isPromoCodeValid(code: string) {
    return this._http.post('api/account/ispromocodevalid', JSON.stringify(code), httpOptions);
  }

  getPromoCode(code: string): Observable<any> {
    return this._http.post('api/account/getPromoCode', JSON.stringify(code), httpOptions);
  }

  getLicenseAssignment(userId: number): Observable<LicenseAssignment[]> {
    return this._http
      .get('api/account/assignment/' + userId)
      .pipe(map(t => t as LicenseAssignment[]));
  }

  redeemRedemptionCode(code: string): Observable<RedemptionCode> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    return this._http
      .post('api/account/redeem-code', JSON.stringify(code), httpOptions)
      .pipe(map(t => new RedemptionCode(t)));
  }

  deleteSession(): Promise<void> {
    const userId = this.user$.value.Id;
    if (userId) {
      this._http.get('api/account/deletesession/' + userId).subscribe(
        data => {
          console.dir(data);
          Promise.resolve();
        },
        error => {
          this._errorObserver.next(JSON.parse(error._body)['Message']);
          return Promise.resolve();
        }
      );
    }
    return Promise.resolve();
  }

  sendEmailConfirmation(userId: number): Observable<any> {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    return this._http.get('api/account/sendconfirmemail/' + userId, httpOptions);
  }

  /**
   * Get user's license info including cards and licenses
   * If user is a Stripe customer, cards will be included
   * Otherwise, only licenses info will be available
   */
  getLicenseInfo(): Observable<LicenseInfo> {
    return this._http.get<LicenseInfo>('api/account/licenseinfo');
  }
}
